import {Textarea} from "@heroui/input";
import {Card, CardBody, CardHeader, cn, NumberInput, SelectItem} from "@heroui/react";
import {Icon} from "@iconify/react";
import React, {useState} from "react";
import BannerImageCrop from "../../components/BannerImageCrop.tsx";
import FileUploader from "../../components/FileUploader.tsx";
import GoogleMapPicker from "../../components/GoogleMapPicker.tsx";
import PAutocompleteCity from "../../components/PAutocompleteCity.tsx";
import PButton from "../../components/PButton.tsx";
import PInput from "../../components/PInput.tsx";
import PPlacesAutocomplete, {PlaceDetails} from "../../components/PPlacesAutocomplete.tsx";
import PSelect from "../../components/PSelect.tsx";
import PSelectCountry from "../../components/PSelectCountry.tsx";
import PSelectTimeZone from "../../components/PSelectTimeZone.tsx";
import services from "../../services";
import {sportsForSurface, liveRegistryMap} from "../../sports/registry.ts";
import {Court, GeoPoint, VedicrishiPlace} from "../../types";
import {ensureObject, maybeUndefinedDefaultKey} from "../../utilities.ts";

export const CURRENCIES = ['AED', 'EUR', 'GBP', 'USD'];

// The editor's sport list (key → label), from the sport registry's 'editor' surface, read from the
// live registry on every access (flag OFF: today's list, plus rugby/hockey/gaming under
// VITE_FEATURE_ENABLE_ADDITIONAL_SPORTS).
export const SPORTS_LIST: Record<string, string> = liveRegistryMap(() => Object.fromEntries(
    sportsForSurface('editor').map((entry) => [entry.key, entry.label]),
));

export const AMENITIES_LIST = {
    WiFi:            'WiFi',
    DisabledAccess:  'Disabled Access',
    EquipmentRental: 'Equipment Rental',
    FreeParking:     'Free Parking',
    PrivateParking:  'Private Parking',
    Store:           'Store',
    Restaurant:      'Restaurant',
    Cafeteria:       'Cafeteria',
    SnackBar:        'Snack Bar',
    ChangingRoom:    'Changing Room',
    Lockers:         'Lockers',
}

export const OPENING_DAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday', '* Holidays'];
const ALL_HOURS = [
    '00:00',
    '01:00',
    '02:00',
    '03:00',
    '04:00',
    '05:00',
    '06:00',
    '07:00',
    '08:00',
    '09:00',
    '10:00',
    '11:00',
    '12:00',
    '13:00',
    '14:00',
    '15:00',
    '16:00',
    '17:00',
    '18:00',
    '19:00',
    '20:00',
    '21:00',
    '22:00',
    '23:00',
    '24:00',
];

function ImageUploadWithCrop({label, buttonLabel, defaultImageUrl, entity, setEntity}: {
    label: string; buttonLabel: string; defaultImageUrl: string; entity: any; setEntity: (e: any) => void;
}) {
    const [rawImageUrl, setRawImageUrl] = useState<string | null>(null);
    const [isCropping, setIsCropping] = useState(false);

    const image_url = entity['thumb_url'] || entity['image_url'] || defaultImageUrl;

    const handleUploaded = (data: any) => {
        setRawImageUrl(data.image_url);
        setIsCropping(true);
    };

    const handleCropSave = async (crop: {x: number; y: number; width: number; height: number}) => {
        if (!rawImageUrl) return;
        const result = await services.core.bannerCrop(rawImageUrl, crop);
        if (result.banner_url) {
            setEntity({...entity, image_url: result.banner_url, thumb_url: result.banner_url});
        }
        setIsCropping(false);
        setRawImageUrl(null);
    };

    const handleCropCancel = () => {
        // Use the raw image without crop
        if (rawImageUrl) {
            setEntity({...entity, image_url: rawImageUrl, thumb_url: rawImageUrl});
        }
        setIsCropping(false);
        setRawImageUrl(null);
    };

    if (isCropping && rawImageUrl) {
        return <div className="mt-2! mb-2">
            <label className="dark:text-white text-small block mb-1">{label}</label>
            <BannerImageCrop
                imgSrc={rawImageUrl}
                aspect={1}
                onSave={handleCropSave}
                onCancel={handleCropCancel}
            />
        </div>;
    }

    return <div className="mt-2! mb-2">
        <label className="dark:text-white text-small block mb-1">{label}</label>
        <div className="flex gap-2">
            {!!image_url && <img src={image_url} className="max-h-10 rounded" alt="Uploaded image" />}
            <FileUploader className="flex-1"
                          category="avatars"
                          buttonText={image_url ? "Change" : buttonLabel}
                          onUploaded={handleUploaded} />
            {!!image_url && <PButton
                color="danger" variant="bordered" isIconOnly
                onPress={() => setEntity({...entity, image_url: undefined, thumb_url: undefined})}
            ><Icon icon="material-symbols-light:delete-outline" className="text-2xl" /></PButton>}
        </div>
    </div>;
}

export default function useEntityEditor<T extends { meta: object }>(defaults: Partial<T> = {}) {
    const [entity, setEntity] = useState<T>({
        meta: {},
        ...defaults,
    } as T);

    function input(field: keyof T, label: string, defaultValue = '') {
        return <PInput label={label} placeholder="" type="text"
                       classNames={{label: "w-full"}}
                       isRequired={true}
                       defaultValue={String(entity[field] || defaultValue || '')}
                       onBlur={(e) => {
                           entity[field] = e.target.value as any;
                           setEntity({...entity});
                       }}
        />
    }

    function coordinates(field: keyof T, label: string, defaultValue: GeoPoint | undefined) {
        return (
            <GoogleMapPicker
                label={label}
                value={defaultValue}
                onChange={(point) => setEntity({...entity, [field]: point})}
            />
        );
    }

    function textarea(label: string, field: keyof T, defaultText = '', placeholder = 'Write your text here...') {
        return <Textarea
            className="mt-3!"
            label={label}
            placeholder={placeholder}
            defaultValue={String(entity[field] || defaultText || '')}
            labelPlacement="outside"
            variant="flat"
            onBlur={(e) => {
                entity[field] = (e.target.value || '') as any;
                setEntity({...entity});
            }}
        />
    }

    function metaInput(label: string, field: keyof T['meta'], props = {}, defaultValue = '' as any) {
        return <PInput label={label} placeholder="" type="text"
                       classNames={{label: "w-full"}}
                       isRequired={true}
                       defaultValue={String(entity.meta[field as any] || defaultValue || '')}
                       onBlur={(e) => {
                           entity.meta = {...entity.meta, [field]: e.target.value};
                           setEntity({...entity});
                       }}
                       {...props}
        />
    }

    function metaCountry(label: string, field: keyof T['meta'], defaultValue = '' as any) {
        return <PSelectCountry
            defaultSelectedKey={entity.meta[field as any] || defaultValue || ''}
            onValueChange={(code) => {
                entity['country_code'] = code;
                setEntity({...entity});
            }}
            label={label}
        />
    }

    function countryCity(defaultCountryCode = '', defaultCity = '') {
        const currentCountryCode = entity['country_code'] !== undefined ? entity['country_code'] : defaultCountryCode;
        const currentCity = entity['city'] !== undefined ? entity['city'] : defaultCity;

        return <div className="grid grid-cols-2 gap-4">
            <PSelectCountry
                selectedKeys={currentCountryCode ? [currentCountryCode] : []}
                onValueChange={(code) => {
                    entity['country_code'] = code;
                    setEntity({...entity});
                }}
                label="Country"
            />

            <PAutocompleteCity
                label={'City'}
                countryCode={currentCountryCode}
                selectedKey={currentCity || ''}
                onSelectionChange={(place: VedicrishiPlace) => {
                    if (place) {
                        entity['city'] = place.place;
                        entity['city_coordinates'] = {type: "Point", coordinates: [place.longitude, place.latitude]};
                        setEntity({...entity});
                    } else {
                        entity['city'] = null
                        entity['city_coordinates'] = null
                        setEntity({...entity});
                    }
                }}
            />
        </div>
    }

    function addressSearch(
        label: string,
        coordinatesField: keyof T,
        addressField?: keyof T,
        defaultAddress = '',
        countryCode?: string
    ) {
        const handlePlaceSelect = (place: PlaceDetails | null) => {
            if (place) {
                const updates: Partial<T> = {
                    [coordinatesField]: {
                        type:        'Point',
                        coordinates: [place.geometry.location.lng, place.geometry.location.lat]
                    }
                } as Partial<T>;

                if (addressField) {
                    updates[addressField] = place.formatted_address as any;
                }

                if (place.address_components) {
                    for (const component of place.address_components) {
                        if (component.types.includes('country')) {
                            (updates as any)['country_code'] = component.short_name;
                        }
                        if (component.types.includes('locality')) {
                            (updates as any)['city'] = component.long_name;
                        } else if (component.types.includes('administrative_area_level_1') && !(updates as any)['city']) {
                            (updates as any)['city'] = component.long_name;
                        }
                    }
                }

                setEntity({...entity, ...updates});
            }
        };

        return (
            <PPlacesAutocomplete
                label={label}
                placeholder="Search for a venue, address, or place..."
                defaultValue={addressField ? String(entity[addressField] || defaultAddress || '') : defaultAddress}
                countryCode={countryCode || entity['country_code']}
                onPlaceSelect={handlePlaceSelect}
            />
        );
    }

    function imageUpload(label: string, buttonLabel: string, defaultImageUrl = '') {
        return <ImageUploadWithCrop
            label={label}
            buttonLabel={buttonLabel}
            defaultImageUrl={defaultImageUrl}
            entity={entity}
            setEntity={setEntity}
        />;
    }

    function select(label: string, field: keyof T, choices: Record<string, string> = {}, defaultValue: string | null = '') {
        return <PSelect
            defaultSelectedKeys={maybeUndefinedDefaultKey(entity[field as any] || defaultValue || "")}
            label={label}
            classNames={{label: "w-full", helperWrapper: "hidden!"}}
            onChange={(e) => {
                setEntity({...entity, [field]: (e.target.value || '')});
            }}
        >
            {Object.entries(choices).map(([key, value]) => (<SelectItem key={key} textValue={value}>{value}</SelectItem>))}
        </PSelect>
    }

    function metaSelect(label: string, field: keyof T['meta'], choices: Record<string, string> = {}, defaultValue = '') {
        return <PSelect
            defaultSelectedKeys={maybeUndefinedDefaultKey(entity.meta[field as any] || defaultValue || "")}
            label={label}
            classNames={{label: "w-full", helperWrapper: "hidden!"}}
            onChange={(e) => {
                entity.meta = {...entity.meta, [field]: (e.target.value || '')};
                setEntity({...entity});
            }}
        >
            {Object.entries(choices).map(([key, value]) => (<SelectItem key={key}>{value}</SelectItem>))}
        </PSelect>
    }

    function metaSelectMultiple(label: string, field: keyof T['meta'], choices: Record<string, string> = {}, defaultValue: string[] = []) {
        return <PSelect
            selectionMode={'multiple'}
            defaultSelectedKeys={entity.meta[field as any] || defaultValue || ""}
            label={label}
            classNames={{label: "w-full", helperWrapper: "hidden!", value: "whitespace-normal max-h-5"}}
            onChange={(e) => {
                entity.meta = {...entity.meta, [field]: (e.target.value || '').split(',')};
                setEntity({...entity});
            }}
        >
            {Object.entries(choices).map(([key, value]) => (<SelectItem key={key}>{value}</SelectItem>))}
        </PSelect>
    }

    function metaTextarea(label: string, field: keyof T['meta'], defaultText = '', placeholder = 'Write your text here...') {
        return <Textarea
            className="mt-3!"
            label={label}
            placeholder={placeholder}
            defaultValue={entity.meta[field as any] || defaultText || ""}
            labelPlacement="outside"
            variant="flat"
            onBlur={(e) => {
                entity.meta = {...entity.meta, [field]: (e.target.value || '')};
                setEntity({...entity});
            }}
        />
    }

    function metaSelectSports(label: string, field: keyof T['meta'], defaultValue = []) {
        return metaSelectMultiple(label, field, SPORTS_LIST, defaultValue);
    }

    function metaSelectYesNo(label: string, field: keyof T['meta'], defaultValue = '') {
        return metaSelect(label, field, {
            yes: 'Yes',
            no:  'No',
        }, defaultValue);
    }

    function timezone(label: string, field: keyof T, defaultValue = '') {
        return <PSelectTimeZone defaultSelectedKey={entity[field] || defaultValue || ''}
                                onValueChange={(code) => {
                                    entity[field] = code;
                                    setEntity({...entity});
                                }}
                                label={label}
        />
    }

    function openingHours(label: string, openingHoursPerDay: [string | undefined, string | undefined][], otherCourts: Court[]) {
        const validCourts = otherCourts.filter((court) => court.opening_hours_per_day?.length);

        return <Card className={"mt-2!"}>
            <CardHeader>{label}</CardHeader>
            <CardBody>

                {!!validCourts?.length && <PSelect
                    label={"Fill from other court:"}
                    classNames={{label: "w-full", helperWrapper: "hidden!"}}
                    onSelectionChange={(key) => {
                        console.log("key", key)
                        const court = otherCourts.find((court) => court.uid === key.currentKey);
                        if (court) {
                            setEntity({...entity, opening_hours_per_day: court.opening_hours_per_day});
                        }
                    }}
                >
                    {validCourts.filter((court) => court.opening_hours_per_day?.length).map((value) => (<SelectItem key={value.uid}>{value.name}</SelectItem>))}
                </PSelect>
                }

                <div className={"grid gap-2 grid-cols-2 mt-4"}>
                    {OPENING_DAYS.map((value, index: number) => (<React.Fragment key={value}>
                        <PSelect
                            key={value}
                            label={value}
                            selectedKeys={maybeUndefinedDefaultKey(openingHoursPerDay?.[index]?.[0])}
                            onChange={(e) => {
                                const h = openingHoursPerDay || [];
                                while (h.length < index + 1) {
                                    h.push([undefined, undefined]);
                                }
                                h[index][0] = e.target.value;
                                setEntity({...entity, opening_hours_per_day: h});
                            }}
                        >
                            <>
                                <SelectItem key={''} aria-label={'Empty'}></SelectItem>
                                {ALL_HOURS.map((time) =>
                                    <SelectItem key={time}>{time}</SelectItem>
                                )}
                            </>
                        </PSelect>

                        {openingHoursPerDay && openingHoursPerDay?.[index]?.[0]
                            ? <PSelect
                                label=""
                                aria-label={`${value} closing time`}
                                isInvalid={!!openingHoursPerDay[index]?.[1] && (openingHoursPerDay[index]?.[1] || '') <= (openingHoursPerDay[index]?.[0] || '')}
                                defaultSelectedKeys={maybeUndefinedDefaultKey(openingHoursPerDay[index]?.[1])}
                                onChange={(e) => {
                                    const h = openingHoursPerDay || [];
                                    while (h.length < index + 1) {
                                        h.push([undefined, undefined]);
                                    }
                                    h[index][1] = e.target.value;
                                    setEntity({...entity, opening_hours_per_day: h});
                                }}
                            >
                                {ALL_HOURS.map((time) =>
                                    <SelectItem key={time}>{time}</SelectItem>
                                )}
                            </PSelect>
                            : <span></span>
                        }
                    </React.Fragment>))}
                </div>
            </CardBody>
        </Card>
    }

    function peakHours(label: string, peakHoursPerDay: any[]) {

        const hourSlots = new Array(24).fill(0).map((_, i) => String(i).padStart(2, "0"));

        function toggle(day, hour) {
            const h = (peakHoursPerDay || []).map((a) => Array.isArray(a) ? {} : a);
            while (h.length < day + 1) {
                h.push({});
            }
            h[day][parseInt(hour, 10)] = !h[day][hour];
            setEntity({...entity, peak_hours_per_day: h});
        }

        return <Card className={"mt-2!"}>
            <CardHeader>{label}</CardHeader>
            <CardBody>

                <div className={"overflow-auto"}>
                    <div className={"flex w-full"}>
                        <div className={"w-[150px] shrink-0 flex items-center"}></div>
                        <div className={"flex-1 grid *:text-small *:whitespace-nowrap"} style={{gridTemplateColumns: 'repeat(' + hourSlots.length + ', minmax(40px, 1fr))'}}>
                            {hourSlots.map((hour) => {
                                return <div key={hour} className={cn([
                                    " relative box-content ",
                                ])}>
                                    {String(hour).padStart(2, "0")}:00
                                </div>
                            })}
                        </div>
                    </div>
                    {OPENING_DAYS.map((value, day: number) => (
                        <div className={"flex w-full"} key={value}>
                            <div className={"w-[150px] shrink-0 flex items-center"}>{value}</div>
                            <div className={"flex-1 grid"} style={{gridTemplateColumns: 'repeat(' + hourSlots.length + ', minmax(40px, 1fr))'}}>
                                {hourSlots.map((hour, hourInt) => {
                                    return <div key={hour} className={cn([
                                        "border relative box-content h-6 cursor-pointer hover:bg-primary-100",
                                        peakHoursPerDay?.[day]?.[hourInt] && 'bg-primary!',
                                    ])}
                                                onClick={() => toggle(day, hourInt)}>
                                    </div>
                                })}
                            </div>
                        </div>
                    ))}
                </div>

            </CardBody>
        </Card>
    }

    function pricing(label: string, field: keyof T, defaultPricing: Record<string, number>) {
        return <>
            <div className={"mb-2"}>{label}</div>
            <div className={"grid gap-2 grid-cols-4"}>
                {CURRENCIES.map((currency) => (
                    <NumberInput className="mb-4" maxValue={1000}
                                 key={currency}
                                 hideStepper={true}
                                 defaultValue={defaultPricing?.[currency] || undefined}
                                 onValueChange={(amount) => {
                                     const pricing = ensureObject(entity[field] || defaultPricing);

                                     entity[field] = {...pricing, [currency]: amount};
                                     setEntity({...entity});
                                 }}
                                 label={""}
                                 aria-label={currency}
                                 endContent={currency}
                    />
                ))}
            </div>
        </>
    }

    function metaPricing(label: string, field: keyof T['meta'], defaultPricing: Record<string, number>) {
        return <>
            <div className={"mb-2"}>{label}</div>
            <div className={"grid gap-2 grid-cols-4"}>
                {CURRENCIES.map((currency) => (
                    <NumberInput className="mb-4" maxValue={1000}
                                 key={currency}
                                 hideStepper={true}
                                 defaultValue={defaultPricing?.[currency] || undefined}
                                 onValueChange={(amount) => {
                                     const pricing = ensureObject(entity.meta[field as any] || defaultPricing);

                                     entity.meta = {...entity.meta, [field]: {...pricing, [currency]: amount}};
                                     setEntity({...entity});
                                 }}
                                 label={""}
                                 aria-label={currency}
                                 endContent={currency}
                    />
                ))}
            </div>
        </>
    }

    function currency(label: string, field: keyof T, defaultValue: string | undefined) {
        return <PSelect
            defaultSelectedKeys={maybeUndefinedDefaultKey(entity[field as any] || defaultValue || "")}
            label={label}
            classNames={{label: "w-full", helperWrapper: "hidden!"}}
            onChange={(e) => {
                setEntity({...entity, [field]: (e.target.value || '')});
            }}
        >
            {CURRENCIES.map((value) => (<SelectItem key={value}>{value}</SelectItem>))}
        </PSelect>
    }

    return {
        entity,
        setEntity,
        entityEditor: {
            input, textarea, countryCity, imageUpload, select, coordinates, timezone,
            metaInput, metaSelect, metaSelectMultiple, metaTextarea, metaPricing,
            metaSelectSports, metaSelectYesNo, metaCountry,
            openingHours, peakHours, pricing, currency, addressSearch,
        }
    };
}